import React, { useEffect, useState } from "react";
import { ArrowLeft, Check, Save, Send } from "lucide-react";
import { Spinner } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { PageHeader } from "../CMSDocuments/CmsDocumentComponents.js";
import { create_article, get_articles, update_article } from "../../reduxData/cms/cmsAction";
import KnowledgeBaseEditor from "./KnowledgeBaseEditor";
import { KB_CATEGORIES, KB_STATUS } from "./knowledgeBase.constants";
import { buildArticlePayload, isContentEmpty, validateArticle } from "./knowledgeBase.helpers";
import "../../css/cmsDocuments.css";
import "./KnowledgeBase.css";
import "../CMSPages/components/bilingualCms.css";
import { useDispatch, useSelector } from "react-redux";



const EMPTY_FORM = { title: "", content: "", content_nl: "", category: "", publish: false };

function SectionHeading({ number, title, description }) {
  return (
    <div className="cmsd-section-heading">
      <span>{number}</span>
      <div>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
    </div>
  );
}

function Field({ label, error, help, children }) {
  return (
    <label className="cmsd-field">
      <span>{label}</span>
      {children}
      {help && <small>{help}</small>}
      {error && <small className="text-danger">{error}</small>}
    </label>
  );
}

export default function ArticleForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(Boolean(id));
  const [activeLanguage, setActiveLanguage] = useState("EN");
  const contentKey = activeLanguage === "NL" ? "content_nl" : "content";

  const { articles } = useSelector((state) => state.cms);

  useEffect(() => {
    if (id && articles.length === 0) get_articles(dispatch);
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const article = articles.find((item) => String(item.id) === String(id));
    if (!article) return;
    setForm({
      title: article.title || "",
      content: article.content || "",
      content_nl: article.content_nl || "",
      category: article.category || "",
      publish: article.status === KB_STATUS.PUBLISHED,
    });
    setLoading(false);
  }, [id, articles]);

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: "" }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    const validationErrors = validateArticle(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) {
      if (!validationErrors.title && !validationErrors.category) {
        setActiveLanguage(validationErrors.content ? "EN" : "NL");
      }
      return;
    }
    const payload = buildArticlePayload(form);
    setSubmitting(true);
    const ok = id
      ? await update_article(dispatch, id, payload)
      : await create_article(dispatch, payload);
    setSubmitting(false);
    if (ok) navigate("/cms/knowledge-base");
  };

  if (loading) {
    return (
      <main className="cmsd-page">
        <div className="cmsd-state"><Spinner /></div>
      </main>
    );
  }

  return (
    <main className="cmsd-page cmsd-form-page">
      <PageHeader
        title={id ? "Edit Article" : "Add Article"}
        description="Write a help article for the Knowledge Base."
      >
        <Link className="btn btn-light" to="/cms/knowledge-base">
          <ArrowLeft size={17} /> Back
        </Link>
      </PageHeader>
      <div className="cmsd-form-shell">
        <form className="cmsd-document-form" onSubmit={onSubmit} noValidate>
          <section className="cmsd-form-section">
            <SectionHeading number="01" title="Article details" description="Title and category." />
            <div className="cmsd-form-grid">
              <Field label="Title *" error={errors.title}>
                <input
                  className="form-control"
                  value={form.title}
                  onChange={(e) => setField("title", e.target.value)}
                  disabled={submitting}
                />
              </Field>
              <Field label="Category *" error={errors.category}>
                <select
                  className="form-select"
                  value={form.category}
                  onChange={(e) => setField("category", e.target.value)}
                  disabled={submitting}
                >
                  <option value="">Select category</option>
                  {KB_CATEGORIES.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </Field>
            </div>
          </section>
          <section className="cmsd-form-section">
            <SectionHeading
              number="02"
              title="Content"
              description="Add text, images and videos. Use Video to embed a YouTube or Vimeo link."
            />
            <div className="bcms-tabs" role="tablist" style={{ marginBottom: 12 }}>
              {["EN", "NL"].map((language) => {
                const key = language === "NL" ? "content_nl" : "content";
                return (
                  <button
                    key={language}
                    type="button"
                    role="tab"
                    aria-selected={activeLanguage === language}
                    className={activeLanguage === language ? "active" : ""}
                    onClick={() => setActiveLanguage(language)}
                  >
                    <span>{language}</span>
                    {language === "EN" ? "English" : "Nederlands"}
                    {!isContentEmpty(form[key]) && <Check size={14} />}
                  </button>
                );
              })}
            </div>
            <div className="cmsd-field">
              <KnowledgeBaseEditor
                key={contentKey}
                toolbarId={`kb-editor-toolbar-${contentKey}`}
                placeholder={activeLanguage === "NL" ? "Schrijf het artikel…" : "Write the article…"}
                value={form[contentKey]}
                onChange={(html) => setField(contentKey, html)}
                disabled={submitting}
                hasError={Boolean(errors[contentKey])}
              />
              {errors[contentKey] && <small className="text-danger">{errors[contentKey]}</small>}
            </div>
          </section>
          <section className="cmsd-form-section">
            <SectionHeading number="03" title="Publishing" description="Drafts stay hidden until published." />
            <div className={`kb-status${form.publish ? " kb-status--on" : ""}`}>
              <div className="form-check form-switch m-0">
                <input
                  id="kb-publish"
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={form.publish}
                  onChange={(e) => setField("publish", e.target.checked)}
                  disabled={submitting}
                />
                <label className="form-check-label" htmlFor="kb-publish">
                  <strong>{form.publish ? "Published" : "Draft"}</strong>
                  <small>
                    {form.publish ? "Visible to users as soon as you save." : "Saved, but not visible to users."}
                  </small>
                </label>
              </div>
            </div>
          </section>
          <footer className="cmsd-publish-footer">
            <div>
              <strong>Ready to save?</strong>
              <small>Title, category and both English and Dutch content are required.</small>
            </div>
            <div>
              <Link className="btn btn-light" to="/cms/knowledge-base">Cancel</Link>
              <button className="btn cmsd-primary" type="submit" disabled={submitting}>
                {submitting ? <Spinner size="sm" /> : form.publish ? <Send size={17} /> : <Save size={17} />}
                Save Article
              </button>
            </div>
          </footer>
        </form>
      </div>
    </main>
  );
}
